import type { TokenInfo } from "@src/components/DefuseSDK/types/base"
import { isBaseToken } from "@src/components/DefuseSDK/utils/token"
import { LIST_TOKENS } from "@src/constants/tokens"
import {
  type EarnVaultOperation,
  isEarnVaultToken,
} from "@src/shared/utils/earnVault"
import { CURATED_EARN_VAULTS } from "./constants"

export {
  classifyEarnVaultOperation,
  type EarnVaultOperation,
  isEarnVaultToken,
} from "@src/shared/utils/earnVault"

/**
 * History/activity callers often use the picker token list, which hides vault
 * share tokens. Merge them back so origin/destination can be classified.
 */
export function withEarnVaultTokens(
  tokenList: TokenInfo[],
  allTokens: TokenInfo[] = LIST_TOKENS
): TokenInfo[] {
  const knownIds = new Set(
    tokenList.flatMap((token) =>
      isBaseToken(token) ? [token.defuseAssetId] : []
    )
  )
  const missingVaults = allTokens.filter(
    (token) =>
      isBaseToken(token) &&
      isEarnVaultToken(token) &&
      !knownIds.has(token.defuseAssetId)
  )
  return missingVaults.length === 0
    ? tokenList
    : [...tokenList, ...missingVaults]
}

/** Human vault label for history/activity (e.g. "Gauntlet USDC"), not the share symbol. */
export function getEarnVaultDisplayName(token: TokenInfo): string {
  const curated = CURATED_EARN_VAULTS.find(
    (vault) => vault.tokenSymbol.toLowerCase() === token.symbol.toLowerCase()
  )
  return curated?.displayName ?? token.name ?? token.symbol
}

export function formatEarnVaultHistoryLabel(
  operation: EarnVaultOperation,
  vaultToken: TokenInfo
): string {
  const name = getEarnVaultDisplayName(vaultToken)
  return operation === "deposit"
    ? `Deposit to ${name}`
    : `Withdraw from ${name}`
}

export function formatEarnVaultActivityTitle(
  operation: EarnVaultOperation,
  vaultToken: TokenInfo,
  paymentAmountFormatted: string,
  paymentTokenSymbol: string
): string {
  const name = getEarnVaultDisplayName(vaultToken)
  return operation === "deposit"
    ? `Deposit ${paymentAmountFormatted} ${paymentTokenSymbol} to ${name}`
    : `Withdraw ${paymentAmountFormatted} ${paymentTokenSymbol} from ${name}`
}
